import { useState } from 'react';
import {
  X, Building2, Plus, Trash2, ChevronUp, Wallet, Hammer, Heart, Clock,
  Wrench, Warehouse, Ambulance, HeartPulse, Layers, AlertTriangle, MapPin,
} from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import {
  STATION_SPECS,
  getCurrentTier,
  getMaxTierLevel,
  getNextTier,
  getVehicleSlots,
  getPatientCapacity,
  isVehicleStationKind,
  effectiveVehicleSlots,
  effectivePatientCapacity,
} from '@/data/stations';
import {
  STATION_EXTENSIONS,
  extensionById,
} from '@/data/stationExtensions';
import { VEHICLE_SPECS } from '@/data/vehicles';
import { formatEuro } from '@/lib/format';
import { formatBuildTime } from '@/data/buildTime';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { StationUpgradeDialog } from '@/components/game/StationUpgradeDialog';
import type {
  Station,
  StationExtensionInstance,
  Vehicle,
  VehicleStatus,
} from '@/state/types';
import type { StationExtension } from '@/data/stationExtensions';

interface StationDetailProps {
  station: Station;
  vehicles: Vehicle[];
  onClose: () => void;
  onRequestBuyVehicle: () => void;
}

const STATUS_COLOR: Record<VehicleStatus, string> = {
  1: 'bg-status-available text-bg-primary',
  2: 'bg-status-available text-bg-primary',
  3: 'bg-status-enroute text-white',
  4: 'bg-status-onscene text-white',
  5: 'bg-accent-dispatch text-white',
  6: 'bg-status-offduty text-white',
  7: 'bg-status-transport text-white',
  8: 'bg-status-transport text-white',
};

type Tab = 'overview' | 'extensions' | 'vehicles' | 'patients';

export function StationDetail({
  station,
  vehicles,
  onClose,
  onRequestBuyVehicle,
}: StationDetailProps) {
  const spec = STATION_SPECS[station.kind];
  const [tab, setTab] = useState<Tab>('overview');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [confirmSell, setConfirmSell] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const gameTime = useGameStore((s) => s.gameTime);
  const credits = useGameStore((s) => s.credits);
  const mode = useGameStore((s) => s.mode);
  const removeStation = useGameStore((s) => s.removeStation);
  const sellStation = useGameStore((s) => s.sellStation);
  const selectDetail = useGameStore((s) => s.selectDetail);
  const pushToast = useGameStore((s) => s.pushToast);
  const buildStationExtension = useGameStore((s) => s.buildStationExtension);
  const allVehicles = useGameStore((s) => s.vehicles);

  const tier = getCurrentTier(station.kind, station.level);
  const maxLevel = getMaxTierLevel(station.kind);
  const atMax = getNextTier(station.kind, station.level) === null;
  const upkeepPerDay = tier.upkeepPerDay;
  const isVehicle = isVehicleStationKind(station.kind);
  const tierSlots = isVehicle ? getVehicleSlots(station.kind, station.level) : 0;
  const slotCap = isVehicle ? effectiveVehicleSlots(station) : 0;
  const tierPatientCap = !isVehicle ? getPatientCapacity(station.kind, station.level) : 0;
  const patientCap = !isVehicle ? effectivePatientCapacity(station) : 0;
  const slotsFull = isVehicle && vehicles.length >= slotCap;

  // Construction state
  const isUnderConstruction =
    Boolean(station.buildStartedAt && station.buildCompleteAt && gameTime < station.buildCompleteAt);
  const buildProgress =
    isUnderConstruction && station.buildStartedAt && station.buildCompleteAt
      ? Math.max(
          0,
          Math.min(
            1,
            (gameTime - station.buildStartedAt) /
              Math.max(1, station.buildCompleteAt - station.buildStartedAt),
          ),
        )
      : 0;
  // Phase 9.5 — sale-value preview for the confirm dialog
  const stationVehiclesForSale = allVehicles.filter((v) => v.stationId === station.id);
  const stationSpent = station.totalSpent ?? 0;
  const stationSaleValue = Math.round(
    stationSpent * (isUnderConstruction ? 0.8 : 0.5),
  );
  const vehicleRefund = stationVehiclesForSale.reduce(
    (acc, v) => acc + Math.round(VEHICLE_SPECS[v.type].cost * 0.5),
    0,
  );
  const totalSaleRefund = stationSaleValue + vehicleRefund;

  return (
    <div className="flex h-full flex-col">
      <header
        className="flex items-center gap-3 px-5 py-4 text-white"
        style={{ background: spec.markerColor }}
      >
        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
          <Building2 className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1 leading-tight">
          <p className="truncate font-display text-lg font-bold">
            {station.realName ?? station.name}
          </p>
          {station.address &&
            (station.address.street || station.address.city) && (
              <p className="flex items-center gap-1 truncate text-[10px] opacity-80">
                <MapPin className="h-2.5 w-2.5" aria-hidden />
                {[
                  [station.address.street, station.address.houseNumber]
                    .filter(Boolean)
                    .join(' '),
                  [station.address.postcode, station.address.city]
                    .filter(Boolean)
                    .join(' '),
                ]
                  .filter(Boolean)
                  .join(', ')}
              </p>
            )}
          <p className="text-xs opacity-80">
            {spec.label} · Stufe {station.level} / {maxLevel}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          className="flex h-8 w-8 items-center justify-center rounded-md bg-white/15 transition hover:bg-white/25"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </header>

      {isUnderConstruction && (
        <div className="border-b border-amber-500/40 bg-amber-500/10 px-5 py-3">
          <div className="flex items-center gap-2 text-xs">
            <Hammer className="h-3.5 w-3.5 text-amber-400" aria-hidden />
            <span className="font-semibold text-amber-300">Im Bau</span>
            <span className="text-amber-200/80">
              · Fertig in ca. {formatRemaining(gameTime, station.buildCompleteAt!)}
            </span>
          </div>
          <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-bg-primary">
            <div
              className="h-full rounded-full bg-amber-400 transition-all"
              style={{ width: `${Math.round(buildProgress * 100)}%` }}
            />
          </div>
        </div>
      )}

      <nav className="border-b border-border-subtle bg-bg-secondary px-5 pt-3">
        <div className="flex gap-1 overflow-x-auto">
          {(isVehicle
            ? (['overview', 'extensions', 'vehicles'] as Tab[])
            : (['overview', 'extensions', 'patients'] as Tab[])
          ).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={[
                'relative whitespace-nowrap px-4 py-2 text-xs font-semibold transition',
                tab === t ? 'text-fg-primary' : 'text-fg-muted hover:text-fg-secondary',
              ].join(' ')}
            >
              {t === 'overview'
                ? 'Übersicht'
                : t === 'extensions'
                  ? `Erweiterungen (${(station.extensions ?? []).length})`
                  : t === 'vehicles'
                    ? `Fahrzeuge (${vehicles.length})`
                    : `Patienten (${(station.currentPatients ?? []).filter((p) => p.freeAt > gameTime).length})`}
              {tab === t && (
                <span
                  className="absolute inset-x-2 -bottom-px h-0.5 rounded-t"
                  style={{ background: spec.markerColor }}
                />
              )}
            </button>
          ))}
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-5">
        {tab === 'overview' && (
          <div className="space-y-4">
            {isVehicle ? (
              <Card>
                <p className="text-[10px] uppercase tracking-wider text-fg-muted">Belegung</p>
                <p className="mt-1 font-display text-2xl font-bold tabular-nums">
                  {vehicles.length} <span className="text-fg-muted">/ {slotCap}</span>
                </p>
                <p className="mt-1 text-xs text-fg-muted">
                  Stationierte Fahrzeuge.{' '}
                  {slotCap > tierSlots ? (
                    <span className="text-status-available">
                      +{slotCap - tierSlots} aus Erweiterungen
                    </span>
                  ) : (
                    <>Slots erweitern sich mit Ausbaustufe und Erweiterungen.</>
                  )}
                </p>
              </Card>
            ) : (
              <Card>
                <p className="text-[10px] uppercase tracking-wider text-fg-muted">
                  Patientenkapazität
                </p>
                <p className="mt-1 font-display text-2xl font-bold tabular-nums">{patientCap}</p>
                <p className="mt-1 text-xs text-fg-muted">
                  Maximale Anzahl Patienten gleichzeitig.{' '}
                  {patientCap > tierPatientCap ? (
                    <span className="text-status-available">
                      +{patientCap - tierPatientCap} aus Erweiterungen
                    </span>
                  ) : null}
                </p>
              </Card>
            )}

            <Card>
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-bg-elevated">
                  <Wallet className="h-4 w-4 text-fg-muted" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-fg-muted">
                    Tägl. Unterhalt
                  </p>
                  <p className="text-sm font-semibold tabular-nums text-accent-emergency">
                    -{formatEuro(upkeepPerDay)}
                  </p>
                </div>
              </div>
            </Card>

            <button
              type="button"
              onClick={() => setUpgradeOpen(true)}
              disabled={atMax || isUnderConstruction}
              className={[
                'flex w-full items-center justify-center gap-2 rounded-md border px-4 py-2.5 text-sm font-semibold transition',
                atMax || isUnderConstruction
                  ? 'cursor-not-allowed border-border-subtle bg-bg-elevated/40 text-fg-muted'
                  : 'border-accent-dispatch/40 bg-accent-dispatch/10 text-accent-dispatch hover:bg-accent-dispatch/15',
              ].join(' ')}
            >
              <ChevronUp className="h-4 w-4" aria-hidden />
              {atMax
                ? 'Höchste Stufe erreicht'
                : isUnderConstruction
                  ? 'Aktueller Bau läuft …'
                  : `Auf Stufe ${station.level + 1} ausbauen`}
            </button>

            <button
              type="button"
              onClick={() => setConfirmRemove(true)}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-border-subtle bg-bg-elevated px-4 py-2.5 text-sm font-semibold text-fg-muted transition hover:border-accent-emergency hover:text-accent-emergency"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
              Wache abreißen
            </button>

            {/* Phase 9.5 — Wache verkaufen */}
            <button
              type="button"
              onClick={() => setConfirmSell(true)}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-accent-emergency/30 bg-accent-emergency/5 px-4 py-2.5 text-sm font-semibold text-accent-emergency transition hover:bg-accent-emergency/10"
            >
              <AlertTriangle className="h-4 w-4" aria-hidden />
              Wache verkaufen
            </button>
          </div>
        )}

        {tab === 'extensions' && (
          <ExtensionsList
            station={station}
            gameTime={gameTime}
            credits={credits}
            mode={mode}
            onBuy={(extId) => {
              const extObj = extensionById(extId);
              if (!extObj) return;
              if (mode !== 'sandbox' && credits < extObj.cost) {
                pushToast({
                  kind: 'error',
                  text: `Nicht genug Credits für „${extObj.name}".`,
                });
                return;
              }
              const ok = buildStationExtension(station.id, extId);
              if (!ok) {
                pushToast({ kind: 'error', text: 'Erweiterung konnte nicht gebaut werden.' });
                return;
              }
              const isInstant =
                mode === 'sandbox' || extObj.buildTimeHours === 0;
              const costSuffix =
                mode !== 'sandbox' ? ` (${formatEuro(extObj.cost)})` : '';
              pushToast({
                kind: 'success',
                text: isInstant
                  ? `Erweiterung „${extObj.name}" gebaut${costSuffix}.`
                  : `„${extObj.name}" wird gebaut – fertig in ${formatBuildTime(extObj.buildTimeHours, mode)}${costSuffix}.`,
              });
            }}
          />
        )}

        {tab === 'vehicles' && (
          <div className="space-y-3">
            {spec.supportsVehicles && (
              <button
                type="button"
                onClick={() => {
                  if (slotsFull) {
                    pushToast({
                      kind: 'error',
                      text: `Alle ${slotCap} Slots belegt. Wache zuerst ausbauen.`,
                    });
                    return;
                  }
                  if (isUnderConstruction) {
                    pushToast({
                      kind: 'error',
                      text: 'Wache wird noch gebaut – Fahrzeugkauf erst nach Fertigstellung möglich.',
                    });
                    return;
                  }
                  onRequestBuyVehicle();
                }}
                disabled={isUnderConstruction || slotsFull}
                className={[
                  'flex w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold text-white transition',
                  isUnderConstruction || slotsFull
                    ? 'cursor-not-allowed bg-bg-elevated text-fg-muted'
                    : 'bg-accent-emergency hover:bg-accent-emergency-hover',
                ].join(' ')}
              >
                <Plus className="h-4 w-4" aria-hidden />
                {slotsFull
                  ? `Slots voll (${vehicles.length} / ${slotCap})`
                  : 'Fahrzeug kaufen'}
              </button>
            )}

            {vehicles.length === 0 ? (
              <p className="rounded-md border border-dashed border-border-subtle py-8 text-center text-xs text-fg-muted">
                Keine Fahrzeuge stationiert.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {vehicles.map((v) => {
                  const vSpec = VEHICLE_SPECS[v.type];
                  return (
                    <li key={v.id}>
                      <button
                        type="button"
                        onClick={() => selectDetail({ kind: 'vehicle', id: v.id })}
                        className="flex w-full items-center gap-2.5 rounded-md border border-border-subtle bg-bg-elevated/50 px-2.5 py-2 text-left transition hover:bg-bg-elevated"
                      >
                        <span
                          className={[
                            'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md font-display text-base font-bold',
                            STATUS_COLOR[v.status],
                          ].join(' ')}
                        >
                          {v.status}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-semibold">{vSpec.label}</p>
                          <p className="truncate text-[10px] text-fg-muted">{vSpec.fullName}</p>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {tab === 'patients' && (
          <PatientsList
            station={station}
            gameTime={gameTime}
            capacity={patientCap}
          />
        )}
      </div>

      <ConfirmDialog
        open={confirmRemove}
        title={`Wache „${station.name}" abreißen?`}
        description="Alle stationierten Fahrzeuge werden ebenfalls entfernt. Diese Aktion kann nicht rückgängig gemacht werden."
        confirmLabel="Abreißen"
        tone="danger"
        onConfirm={() => {
          setConfirmRemove(false);
          removeStation(station.id);
          pushToast({ kind: 'info', text: `Wache „${station.name}" entfernt.` });
        }}
        onCancel={() => setConfirmRemove(false)}
      />

      {/* Phase 9.5 — Sale confirm with refund breakdown. */}
      <ConfirmDialog
        open={confirmSell}
        title={`Wache „${station.name}" verkaufen?`}
        description={
          isUnderConstruction
            ? `Die Wache ist noch im Bau — der Verkauf bringt nur 80 % der bisher gezahlten Bausumme zurück. Erstattung: ${formatEuro(totalSaleRefund)} (Wache ${formatEuro(stationSaleValue)} + Fahrzeuge ${formatEuro(vehicleRefund)}). Diese Aktion ist endgültig.`
            : `Erstattung: ${formatEuro(totalSaleRefund)} (Wache ${formatEuro(stationSaleValue)} + Fahrzeuge ${formatEuro(vehicleRefund)}). Alle stationierten Fahrzeuge werden mit verkauft. Diese Aktion ist endgültig.`
        }
        confirmLabel={`Verkaufen für ${formatEuro(totalSaleRefund)}`}
        tone="danger"
        onConfirm={() => {
          setConfirmSell(false);
          const refund = sellStation(station.id);
          pushToast({
            kind: refund > 0 ? 'success' : 'info',
            text:
              mode === 'sandbox'
                ? `Wache „${station.name}" verkauft (Sandbox: keine Erstattung).`
                : `Wache „${station.name}" verkauft. Erstattung: ${formatEuro(refund)}.`,
          });
        }}
        onCancel={() => setConfirmSell(false)}
      />

      {upgradeOpen && (
        <StationUpgradeDialog station={station} onClose={() => setUpgradeOpen(false)} />
      )}
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border-subtle bg-bg-elevated/40 p-4">{children}</div>
  );
}

interface ExtensionsListProps {
  station: Station;
  gameTime: number;
  credits: number;
  mode: 'normal' | 'realistic' | 'sandbox';
  onBuy: (extensionId: string) => void;
}

function extensionIcon(extId: string) {
  if (extId.includes('workshop')) return Wrench;
  if (extId.includes('ems-extension')) return Ambulance;
  if (extId.includes('vehicle-hall')) return Warehouse;
  if (extId.includes('helipad')) return Layers;
  if (extId.includes('er-extension')) return HeartPulse;
  if (extId.includes('training')) return Hammer;
  return Building2;
}

function ExtensionsList({
  station,
  gameTime,
  credits,
  mode,
  onBuy,
}: ExtensionsListProps) {
  const installed = station.extensions ?? [];
  const installedIds = new Set(installed.map((e) => e.extensionId));
  const allForKind = STATION_EXTENSIONS[station.kind] ?? [];
  // Phase 11.2.4 (N7) — Filter workshop when the tier already has a built-in one.
  const tier = getCurrentTier(station.kind, station.level);
  const hasBuiltIn = tier && 'builtInWorkshop' in tier && tier.builtInWorkshop === true;
  const available = allForKind.filter((e) => {
    if (installedIds.has(e.id)) return false;
    if (hasBuiltIn && e.effects.allowsRepair) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-fg-muted">
          Installiert ({installed.length})
        </p>
        {installed.length === 0 ? (
          <p className="rounded-md border border-dashed border-border-subtle py-6 text-center text-xs text-fg-muted">
            Noch keine Erweiterungen gebaut.
          </p>
        ) : (
          <ul className="space-y-2">
            {installed.map((inst) => (
              <InstalledExtensionCard
                key={inst.id}
                instance={inst}
                gameTime={gameTime}
              />
            ))}
          </ul>
        )}
      </div>

      <div>
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-fg-muted">
          Verfügbar ({available.length})
        </p>
        {available.length === 0 ? (
          <p className="rounded-md border border-dashed border-border-subtle py-6 text-center text-xs text-fg-muted">
            Alle Erweiterungen für diese Wachen-Art bereits gebaut.
          </p>
        ) : (
          <ul className="space-y-2">
            {available.map((ext) => (
              <AvailableExtensionCard
                key={ext.id}
                ext={ext}
                affordable={mode === 'sandbox' || credits >= ext.cost}
                mode={mode}
                onBuy={() => onBuy(ext.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function InstalledExtensionCard({
  instance,
  gameTime,
}: {
  instance: StationExtensionInstance;
  gameTime: number;
}) {
  const ext = extensionById(instance.extensionId);
  if (!ext) return null;
  const Icon = extensionIcon(instance.extensionId);
  const isBuilding =
    Boolean(instance.buildStartedAt && instance.buildCompleteAt) &&
    gameTime < (instance.buildCompleteAt ?? 0);
  const buildProgress =
    isBuilding && instance.buildStartedAt && instance.buildCompleteAt
      ? Math.max(
          0,
          Math.min(
            1,
            (gameTime - instance.buildStartedAt) /
              Math.max(1, instance.buildCompleteAt - instance.buildStartedAt),
          ),
        )
      : 1;

  return (
    <li
      className={[
        'rounded-md border p-3',
        isBuilding
          ? 'border-amber-500/40 bg-amber-500/5'
          : 'border-status-available/40 bg-status-available/5',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <span
          className={[
            'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md',
            isBuilding
              ? 'bg-amber-500/15 text-amber-400'
              : 'bg-status-available/15 text-status-available',
          ].join(' ')}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-fg-primary">{ext.name}</p>
          <p className="text-[10px] text-fg-muted">{ext.description}</p>
          {isBuilding && instance.buildCompleteAt && (
            <p className="mt-1 text-[10px] font-semibold text-amber-300">
              Im Bau · fertig in {formatRemaining(gameTime, instance.buildCompleteAt)}
            </p>
          )}
          {!isBuilding && (
            <p className="mt-1 text-[10px] text-status-available">Aktiv</p>
          )}
        </div>
      </div>
      {isBuilding && (
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-bg-primary">
          <div
            className="h-full rounded-full bg-amber-400 transition-all"
            style={{ width: `${Math.round(buildProgress * 100)}%` }}
          />
        </div>
      )}
    </li>
  );
}

function AvailableExtensionCard({
  ext,
  affordable,
  mode,
  onBuy,
}: {
  ext: StationExtension;
  affordable: boolean;
  mode: 'normal' | 'realistic' | 'sandbox';
  onBuy: () => void;
}) {
  const Icon = extensionIcon(ext.id);
  return (
    <li className="rounded-md border border-border-subtle bg-bg-elevated/40 p-3">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-bg-elevated text-fg-secondary">
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-fg-primary">{ext.name}</p>
          <p className="text-[10px] text-fg-muted">{ext.description}</p>
          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-fg-muted">
            <span>
              <span className="text-fg-secondary">Kosten:</span>{' '}
              <span className="font-semibold tabular-nums text-fg-primary">
                {formatEuro(ext.cost)}
              </span>
            </span>
            <span>
              <span className="text-fg-secondary">Unterhalt:</span>{' '}
              <span className="tabular-nums text-accent-emergency">
                -{formatEuro(ext.upkeepPerDay)}/Tag
              </span>
            </span>
            <span>
              <span className="text-fg-secondary">Bauzeit:</span>{' '}
              <span className="tabular-nums">
                {ext.buildTimeHours === 0
                  ? 'sofort'
                  : formatBuildTime(ext.buildTimeHours, mode)}
              </span>
            </span>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onBuy}
        disabled={!affordable}
        className={[
          'mt-2 flex w-full items-center justify-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold transition',
          affordable
            ? 'bg-accent-emergency text-white hover:bg-accent-emergency-hover'
            : 'cursor-not-allowed bg-bg-elevated text-fg-muted',
        ].join(' ')}
      >
        <Plus className="h-3.5 w-3.5" aria-hidden />
        {affordable ? 'Bauen' : 'Credits fehlen'}
      </button>
    </li>
  );
}

/** "1 min 23 s" / "12 s" / "0 s". */
function formatRemaining(now: number, completeAt: number): string {
  const ms = Math.max(0, completeAt - now);
  if (ms < 1000) return '< 1 s';
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec} s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return sec === 0 ? `${min} min` : `${min} min ${sec} s`;
}

interface PatientsListProps {
  station: Station;
  gameTime: number;
  capacity: number;
}

function PatientsList({ station, gameTime, capacity }: PatientsListProps) {
  const patients = (station.currentPatients ?? []).filter((p) => p.freeAt > gameTime);
  const free = capacity - patients.length;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-3 text-center">
          <p className="text-[10px] uppercase tracking-wider text-fg-muted">Belegt</p>
          <p className="mt-0.5 font-display text-2xl font-bold tabular-nums text-accent-emergency">
            {patients.length}
          </p>
        </div>
        <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-3 text-center">
          <p className="text-[10px] uppercase tracking-wider text-fg-muted">Frei</p>
          <p className="mt-0.5 font-display text-2xl font-bold tabular-nums text-status-available">
            {free}
          </p>
        </div>
      </div>

      {patients.length === 0 ? (
        <p className="rounded-md border border-dashed border-border-subtle py-8 text-center text-xs text-fg-muted">
          Keine Patienten in Behandlung.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {patients
            .slice()
            .sort((a, b) => a.freeAt - b.freeAt)
            .map((p) => {
              const total = Math.max(1, p.freeAt - p.admittedAt);
              const remaining = Math.max(0, p.freeAt - gameTime);
              const pct = Math.max(0, Math.min(1, 1 - remaining / total));
              return (
                <li
                  key={p.id}
                  className="rounded-md border border-border-subtle bg-bg-elevated/50 px-3 py-2"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-status-available/15 text-status-available">
                      <Heart className="h-3.5 w-3.5" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-semibold">Patient {p.id.slice(-4)}</p>
                      <p className="text-[10px] text-fg-muted">
                        Aufgenommen {formatRemaining(0, gameTime - p.admittedAt)} her
                      </p>
                    </div>
                    <span className="flex-shrink-0 rounded-full bg-bg-elevated px-2 py-0.5 text-[10px] font-semibold tabular-nums text-fg-secondary">
                      <Clock className="mr-0.5 inline h-3 w-3" aria-hidden />
                      {formatRemaining(gameTime, p.freeAt)}
                    </span>
                  </div>
                  <div className="mt-2 h-1 overflow-hidden rounded-full bg-bg-primary">
                    <div
                      className="h-full rounded-full bg-status-available transition-all"
                      style={{ width: `${Math.round(pct * 100)}%` }}
                    />
                  </div>
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
}
